/**
 * Page dimensions for a docx-preview render.
 *
 * Signature fields are stored in PDF points, so any overlay drawn over the
 * rendered .docx needs each page's true point size to map them. docx-preview
 * already computes this — it writes the section's page size straight into the
 * element's inline style in points (its `Dxa` length unit is twips × 0.05 = pt).
 *
 * Reading that back is deliberately preferred over re-parsing `w:sectPr/w:pgSz`
 * from the XML: resolving section properties correctly means handling body-level
 * defaults, inheritance between sections, and `w:orient="landscape"`. docx-preview
 * has already done all of that, and the overlay must agree with *what is
 * rendered* rather than with the file.
 */

/** CSS pixels → points at 96 DPI. */
const PT_PER_PX = 0.75;

const US_LETTER = { widthPt: 612, heightPt: 792 };

export interface PageDimsPt {
  widthPt: number;
  heightPt: number;
}

/** Parse a CSS length in `pt` or `px` to points. Returns null if neither. */
export function parseLengthToPt(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^\s*(-?[\d.]+)\s*(pt|px)\s*$/i.exec(value);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount)) return null;
  return match[2].toLowerCase() === 'pt' ? amount : amount * PT_PER_PX;
}

/**
 * Portrait A4 is 1.41, US-Letter 1.29; landscape A4 is 0.71. Anything outside
 * this band is not a page size — see the `min-height` caveat below.
 */
const MIN_PAGE_RATIO = 0.5;
const MAX_PAGE_RATIO = 3;

/**
 * The true page size of a rendered `section.docxpv`, in points.
 *
 * Falls back to measuring the element (dividing out the wrapper's CSS scale)
 * and finally to US-Letter, so a caller always gets usable numbers.
 */
export function pageDimsFromSection(section: HTMLElement, appliedScale = 1): PageDimsPt {
  const widthPt = parseLengthToPt(section.style.width);
  // Sections are laid out with `min-height`, so that carries the page height —
  // but only if nothing overwrote it. docx-preview sets it at creation and then
  // applies the document-level style to the same element, and it uses
  // `min-height` for line spacing elsewhere. A clobbered value reads as a
  // page a few points tall, so sanity-check the aspect before trusting it.
  const heightPt =
    parseLengthToPt(section.style.minHeight) ?? parseLengthToPt(section.style.height);

  if (widthPt && heightPt) {
    const ratio = heightPt / widthPt;
    if (ratio > MIN_PAGE_RATIO && ratio < MAX_PAGE_RATIO) return { widthPt, heightPt };
  }

  const rect = section.getBoundingClientRect();
  const scale = appliedScale || 1;
  if (rect.width > 0 && rect.height > 0) {
    return {
      widthPt: (rect.width / scale) * PT_PER_PX,
      heightPt: (rect.height / scale) * PT_PER_PX,
    };
  }

  return US_LETTER;
}
